/**
 * Entry point: map, search, information panel, layers drawer, tools, controls,
 * status bar (WGS84 + VN-2000 under the cursor), keyboard shortcuts, help dialog, URL state.
 */
import { $, icon, toast, copyText, initTooltips, guardClipFrames, rafThrottle, isMobile, setPressed, canHover, lazyImport } from './ui.js';
import { onBusy } from './api.js';
import { bus, prefs, savePrefs, parseHash, writeHash, theme, setTheme, basemapId } from './state.js';
import { DEFAULT_VIEW, HANOI_BBOX, PLAN_LAYERS } from './config.js';
import { PlanMap } from './map.js';
import * as panel from './panel.js';
import { initSearch, focusSearch } from './search.js';
import * as layers from './layers.js';
import * as tools from './tools.js';
import * as dock from './dock.js';
import { initLabels } from './labels.js';
import { initRowLabels } from './rowlabels.js';
import { initPlaces } from './places.js';
import { initSdd } from './sddlayer.js';
import { initHelp, openHelp } from './help.js';
import { initGia, giaHint } from './gia.js';
import { initHoverCard } from './hovercard.js';
import { initCatalog, openCatalog, isCatalogOpen, showEntry, hasApprox, clearApprox, onMapClick as approxClick } from './catalog.js';
import { isTourOpen } from './tour.js';
import { toVN2000, formatVN } from './core/vn2000.js';
import { bboxContains } from './core/geo.js';
import { initTelemetry, act } from './telemetry.js';
import { initStatus } from './status.js';
import { initGps } from './gps.js';
import { savedList, savedById } from './savedstore.js';
import { initPwa, isStandalone } from './pwa.js';
import { computeInfo } from './compute.js';

const waitForMapLibre = () => new Promise((resolve, reject) => {
  if (window.maplibregl) return resolve();
  const s = document.querySelector('script[src*="maplibre-gl"]');
  s?.addEventListener('load', () => resolve(), { once: true });
  s?.addEventListener('error', () => reject(new Error('Không tải được thư viện bản đồ')), { once: true });
  setTimeout(() => (window.maplibregl ? resolve() : reject(new Error('Hết thời gian tải thư viện bản đồ'))), 20000);
});

let pm = null;

boot().catch((e) => {
  console.error(e);
  toast(e.message || 'Không khởi tạo được bản đồ', { type: 'err', ms: 8000 });
});

/** /s/<id> pages carry the shared map state (cloudflare/share.js): restore it as a normal hash. */
function sharedEntry() {
  const m = document.querySelector('meta[name="qh-share"]');
  if (m?.content) { history.replaceState(null, '', '/#' + m.content.replace(/^#/, '')); return 'share'; }
  if (document.querySelector('meta[name="qh-share-missing"]')) {
    history.replaceState(null, '', '/' + location.hash);
    setTimeout(() => toast('Liên kết chia sẻ không tồn tại hoặc đã bị xoá. Đang mở bản đồ.', { type: 'warn', ms: 5000 }), 600);
    return 'share-missing';
  }
  return null;
}

async function boot() {
  const shared = sharedEntry();
  const h = parseHash();
  initTelemetry({ entry: shared || (h.pt ? 'pt' : h.view ? 'view' : isStandalone() ? 'app' : 'direct') });
  if (h.plan !== undefined) prefs.plan = h.plan;
  if (h.boundaries) prefs.boundaries = h.boundaries;
  if (h.basemap) prefs.basemap = h.basemap;
  if (h.sddPeriod) prefs.sddPeriod = h.sddPeriod;
  const view = h.view || (h.pt ? { lat: h.pt.lat, lng: h.pt.lng, zoom: 17 } : null) || prefs.view || DEFAULT_VIEW;

  initTooltips();
  guardClipFrames();
  initProgress();
  initSearch({ onPick, getCenter: () => (pm?.map ? pm.map.getCenter() : DEFAULT_VIEW), openSaved: () => openSaved() });
  initHelp({
    actions: {
      pick: () => toast('Nhấp vào một vị trí bất kỳ trên bản đồ', { type: 'info', ms: 3200 }),
      search: () => focusSearch(),
      transport: () => layers.setPlan('gt', { announce: true }),
      basemap: () => dock.openTray('basemap'),
      parcel: () => tools.toggle('parcel'),
      measure: () => tools.toggle('measure'),
      compare: () => tools.toggle('compare'),
    },
  });
  syncThemeButton();

  await waitForMapLibre();
  pm = new PlanMap($('#map'), { view, basemap: basemapId(), plan: prefs.plan, boundaries: prefs.boundaries, opacity: prefs.opacity, places: prefs.places, gia: prefs.gia, saved: savedOnMap() });
  panel.initPanel(pm);
  layers.initLayers(pm);
  dock.initDock(pm);
  tools.initTools(pm);
  initCatalog(pm);
  initControls();
  await pm.ready;
  const map = pm.map;
  initLabels(pm);
  initRowLabels(pm);
  initPlaces(pm);
  initSdd(pm);
  initGia(pm, { padding: () => panel.viewPadding() });
  if (new URLSearchParams(location.search).has('debug')) { window.__pm = pm; window.__compute = computeInfo; }
  tools.attachMap();
  initMapEvents(map);
  // vector QH 1/2000: card under a resting pointer (desktop); touch keeps tap → panel
  initHoverCard(pm, { busy: () => tools.capturing() || isTourOpen(), extra: (ll) => giaHint(ll.lat, ll.lng) });
  initStatusBar(map);
  initAttributionFit(map);
  initKeyboard();
  initSavedLayer(map);
  initGps({ planMap: pm, button: $('#btn-locate'), getPadding: () => panel.viewPadding(), onFirstFix: (ll) => panel.openAt({ lat: ll.lat, lng: ll.lng }, { kind: 'gps', fly: false }) });
  initStatus({ retry: retryUpstream });
  initUsageEvents();
  document.body.classList.add('map-ready');
  writeHash({ view: { lat: map.getCenter().lat, lng: map.getCenter().lng, zoom: map.getZoom(), bearing: map.getBearing() } });
  firstVisit();
  // installable app: service worker, install suggestion, manifest shortcuts (/?go=…)
  initPwa({ actions: { search: () => focusSearch(), saved: () => openSaved(), locate: () => $('#btn-locate')?.click() } });

  if (h.pt && bboxContains(HANOI_BBOX, h.pt.lng, h.pt.lat)) panel.openAt(h.pt, { fly: false });
  addEventListener('hashchange', () => {
    const n = parseHash();
    const cur = panel.currentSelection();
    if (n.pt && (!cur || Math.abs(cur.lat - n.pt.lat) > 1e-6 || Math.abs(cur.lng - n.pt.lng) > 1e-6)) panel.openAt(n.pt, {});
  });
}

/** Anonymous feature counters (telemetry.js; nothing is sent when statistics are off). */
function initUsageEvents() {
  bus.on('plan', (p) => act('layer', p ?? 'off'));
  bus.on('basemap', (b) => act('basemap', b));
  bus.on('tool-state', (n) => { if (n) act('tool', n); });
  bus.on('theme', (t) => act('theme', t));
  bus.on('labels', (on) => act('labels', on ? 'on' : 'off'));
  bus.on('sdd-period', (p) => act('sdd', p));
}

/* ---------------- search ---------------- */

async function onPick(r) {
  if (!pm) await waitForMapLibre();
  await pm.ready;
  // catalogue entries (projects from the search box) share the catalogue's map actions
  act('search', r.entry ? 'project' : r.kind || 'place');
  if (r.entry) { showEntry(r.entry); return; }
  clearApprox();
  if (tools.capturing()) tools.stop();
  panel.openAt({ lat: r.lat, lng: r.lng }, { label: r.label, sub: r.sub, kind: 'search', zoom: r.zoom, fly: !r.bbox, project: r.project });
  if (r.bbox) pm.fitBBox(r.bbox, { padding: panel.viewPadding(), maxZoom: r.kind === 'project' ? 16 : 17.5 });
  if (!bboxContains(HANOI_BBOX, r.lng, r.lat)) toast('Vị trí nằm ngoài phạm vi dữ liệu quy hoạch Hà Nội', { type: 'warn', ms: 4000 });
}

/* ---------------- progress bar (API + planning tiles) ---------------- */

function initProgress() {
  const bar = $('#progress');
  let api = false, tiles = false, timer = 0, cap = 0;
  const update = () => {
    clearTimeout(timer);
    if (api || tiles) {
      timer = setTimeout(() => {
        bar.classList.add('on');
        // never sweep for ever: slow official tiles keep loading quietly and every section shows its own state
        if (!cap) cap = setTimeout(() => bar.classList.remove('on'), 12000);
      }, api ? 150 : 600);
    } else {
      clearTimeout(cap); cap = 0;
      bar.classList.remove('on');
    }
  };
  onBusy((on) => { api = on; update(); });
  bus.on('tiles-busy', (on) => { tiles = on; update(); });
}

/* ---------------- map events ---------------- */

function initMapEvents(map) {
  map.on('click', (e) => {
    if (tools.capturing()) return;
    if (isMobile() && layers.isDrawerOpen()) { layers.closeDrawer(); return; }
    const hit = map.getLayer('qh-saved') && map.getLayoutProperty('qh-saved', 'visibility') !== 'none'
      ? map.queryRenderedFeatures(e.point, { layers: ['qh-saved'] })[0] : null;
    const place = hit && savedById(hit.properties.id);
    if (place) { act('saved', 'map-click'); openPlace(place, { fly: false }); return; }
    const ll = e.lngLat;
    approxClick(ll);
    if (!bboxContains(HANOI_BBOX, ll.lng, ll.lat)) { toast('Vị trí nằm ngoài phạm vi dữ liệu quy hoạch Hà Nội', { type: 'info' }); return; }
    panel.openAt({ lat: ll.lat, lng: ll.lng }, { kind: 'point', fly: false, keepTab: true });
    ensureVisible(map, ll);
  });

  // planning tiles from the upstream can be slow when cold: show progress after a short delay
  let loading = false, poll = 0;
  const tilesDone = () => { if (!loading) return; loading = false; clearInterval(poll); bus.emit('tiles-busy', false); };
  map.on('dataloading', (e) => {
    if (!loading && e.sourceId && /^qh-(plan|bound)-/.test(e.sourceId)) {
      loading = true; bus.emit('tiles-busy', true);
      // 'idle' waits for every transition and basemap tile too: also poll the tile state directly
      clearInterval(poll);
      poll = setInterval(() => { if (map.areTilesLoaded()) tilesDone(); }, 800);
    }
  });
  map.on('idle', tilesDone);

  map.on('moveend', () => {
    const c = map.getCenter();
    const v = { lat: c.lat, lng: c.lng, zoom: map.getZoom(), bearing: map.getBearing() };
    writeHash({ view: v });
    prefs.view = v;
    savePrefs();
  });

  map.on('error', (e) => {
    const id = e.sourceId || '';
    if (/^qh-/.test(id) && !map.__tileErr) {
      map.__tileErr = true;
      toast('Một số mảnh bản đồ quy hoạch tải chậm, đang thử lại…', { type: 'warn', ms: 3500 });
      setTimeout(() => { map.__tileErr = false; }, 30000);
    }
  });
}

/** Collapse the (compact) map attribution to its info button whenever the dock would cover it. */
function initAttributionFit(map) {
  const check = () => {
    const a = map.getContainer().querySelector('.maplibregl-ctrl-attrib.maplibregl-compact-show');
    const d = $('#dock');
    if (!a || !d || d.hidden) return;
    const ra = a.getBoundingClientRect(), rd = d.getBoundingClientRect();
    if (rd.width && rd.right + 8 > ra.left && rd.left < ra.right && rd.top < ra.bottom && rd.bottom > ra.top) a.classList.remove('maplibregl-compact-show');
  };
  let t = 0;
  const later = () => { clearTimeout(t); t = setTimeout(check, 420); }; // after the dock's slide transition
  new MutationObserver(later).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  addEventListener('resize', later);
  map.on('idle', check);
}

/** Pan only if the clicked point would end up hidden behind the panel / sheet. */
function ensureVisible(map, ll) {
  setTimeout(() => {
    const p = map.project(ll);
    const pad = panel.viewPadding();
    const w = map.getCanvas().clientWidth, hgt = map.getCanvas().clientHeight;
    const hidden = p.x < (pad.left || 0) + 24 || p.x > w - (pad.right || 0) - 12 || p.y < (pad.top || 0) + 24 || p.y > hgt - (pad.bottom || 0) - 24;
    if (hidden) pm.goTo({ lat: ll.lat, lng: ll.lng }, { zoom: map.getZoom(), padding: pad });
  }, isMobile() ? 60 : 0);
}

/* ---------------- controls ---------------- */

function initControls() {
  $('#btn-zoom-in').addEventListener('click', () => pm.map?.zoomIn({ duration: 280 }));
  $('#btn-zoom-out').addEventListener('click', () => pm.map?.zoomOut({ duration: 280 }));
  $('#btn-north').addEventListener('click', () => pm.map?.easeTo({ bearing: 0, pitch: 0, duration: 450 }));
  $('#btn-theme').addEventListener('click', toggleTheme);
  $('#btn-fullscreen').addEventListener('click', toggleFullscreen);
  $('#btn-help').addEventListener('click', () => openHelp());
  $('#btn-catalog')?.addEventListener('click', () => openCatalog());
  $('#btn-help-m')?.addEventListener('click', () => openHelp());
  $('#btn-saved')?.addEventListener('click', () => openSaved());
  $('#btn-saved-m')?.addEventListener('click', () => openSaved());
  bus.on('saved-open', (o) => openSaved({ focusId: o?.id }));
  bus.on('saved', syncSavedBadge);
  syncSavedBadge();
  document.addEventListener('fullscreenchange', () => {
    const on = !!document.fullscreenElement;
    const b = $('#btn-fullscreen');
    setPressed(b, on);
    b.innerHTML = icon(on ? 'minimize' : 'maximize');
    b.setAttribute('data-tip', on ? 'Thoát toàn màn hình' : 'Toàn màn hình');
  });
  if (!document.fullscreenEnabled) $('#btn-fullscreen').hidden = true;
}

/* ---------------- saved places ---------------- */

const SAVED_MAP_KEY = 'qh.saved.map';
function savedOnMap() { try { return localStorage.getItem(SAVED_MAP_KEY) !== 'off'; } catch { return true; } }
function setSavedOnMap(on) {
  try { if (on) localStorage.removeItem(SAVED_MAP_KEY); else localStorage.setItem(SAVED_MAP_KEY, 'off'); } catch { /* private mode */ }
  pm?.setSavedVisible(on);
}

function savedFC() {
  const list = savedList();
  return {
    type: 'FeatureCollection',
    features: list.map((p, i) => ({ type: 'Feature', properties: { id: p.id, n: p.name.length > 34 ? p.name.slice(0, 32) + '…' : p.name, o: i }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })),
  };
}

function initSavedLayer(map) {
  pm.setData('saved', savedFC());
  bus.on('saved', () => pm.setData('saved', savedFC()));
  map.on('mouseenter', 'qh-saved', () => { if (!tools.capturing()) map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', 'qh-saved', () => { map.getCanvas().style.cursor = ''; });
}

function syncSavedBadge() {
  const n = savedList().length;
  for (const b of [$('#btn-saved'), $('#btn-saved-m')]) {
    if (!b) continue;
    const badge = b.querySelector('.badge-n');
    if (badge) { badge.textContent = n > 99 ? '99+' : String(n); badge.hidden = !n; }
    b.setAttribute('data-tip', n ? `Địa điểm đã lưu (${n})  (S)` : 'Địa điểm đã lưu  (S)');
  }
}

function openPlace(p, { fly = true } = {}) {
  clearApprox();
  if (tools.capturing()) tools.stop();
  panel.openAt({ lat: p.lat, lng: p.lng }, { label: p.name, sub: p.addr || '', kind: 'saved', fly, zoom: Math.max(pm.map.getZoom(), 17) });
}

function openSaved(opts = {}) {
  lazyImport(() => import('./saved.js')).then((m) => m.openSaved({
    ...opts,
    open: (p) => openPlace(p),
    mapToggle: { get: savedOnMap, set: setSavedOnMap },
  })).catch(() => {});
}

/** "Thử lại" in the outage banner: reload planning tiles and the open selection. */
function retryUpstream() {
  const m = pm?.map;
  for (const l of m?.getStyle()?.layers || []) {
    const src = l.type === 'raster' && /^qh-(plan|bound|sdd)/.test(l.source) ? m.getSource(l.source) : null;
    if (src?.setTiles && src.tiles) src.setTiles([...src.tiles]); // same URLs: refetches the failed tiles
  }
  const s = panel.currentSelection();
  if (s && s.kind !== 'parcel') panel.openAt({ lat: s.lat, lng: s.lng }, { label: s.address?.title, sub: s.address?.sub, kind: s.kind, fly: false, keepTab: true });
}

function toggleTheme() {
  setTheme(theme() === 'dark' ? 'light' : 'dark');
  syncThemeButton();
}

function syncThemeButton() {
  const b = $('#btn-theme');
  const dark = theme() === 'dark';
  b.innerHTML = icon(dark ? 'sun' : 'moon');
  b.setAttribute('data-tip', dark ? 'Giao diện sáng  (T)' : 'Giao diện tối  (T)');
}
bus.on('theme', () => { pm?.refreshColors(); });

function toggleFullscreen() {
  if (document.fullscreenElement) document.exitFullscreen?.();
  else document.documentElement.requestFullscreen?.().catch(() => toast('Không bật được chế độ toàn màn hình', { type: 'err' }));
}

/* ---------------- status bar ---------------- */

function initStatusBar(map) {
  const cur = $('#sb-cursor span');
  const vn = $('#sb-vn span');
  const zoom = $('#sb-zoom');
  const compass = $('#compass-icon');
  const show = (ll) => {
    cur.textContent = `${ll.lat.toFixed(5)}, ${ll.lng.toFixed(5)}`;
    const v = toVN2000(ll.lat, ll.lng, 'hn-105');
    vn.textContent = `X ${formatVN(v.x, 1)} · Y ${formatVN(v.y, 1)}`;
  };
  // the cursor position while the pointer is over the map, else the map centre (also while the camera
  // moves on its own: GPS follow, search fly-to, keyboard, wheel zoom under a still pointer)
  let pt = null;
  const onMove = rafThrottle((e) => { pt = e.point; show(e.lngLat); });
  const center = () => show(map.getCenter());
  if (canHover()) {
    map.on('mousemove', onMove);
    map.getCanvas().addEventListener('mouseleave', () => { pt = null; center(); });
  }
  map.on('move', rafThrottle(() => show(pt ? map.unproject(pt) : map.getCenter())));
  const z = () => { zoom.textContent = `z ${map.getZoom().toFixed(1)}`; };
  const rot = () => { compass.style.transform = `rotate(${-map.getBearing()}deg)`; $('#btn-north').classList.toggle('rotated', Math.abs(map.getBearing()) > 0.5); };
  map.on('zoom', rafThrottle(z));
  map.on('rotate', rafThrottle(rot));
  z(); rot(); center();
  $('#sb-vn').addEventListener('click', () => {
    const c = map.getCenter();
    const v = toVN2000(c.lat, c.lng, 'hn-105');
    copyText(`X=${v.x.toFixed(3)} Y=${v.y.toFixed(3)}`, 'toạ độ VN-2000 tâm bản đồ');
  });
}

/* ---------------- keyboard ---------------- */

function initKeyboard() {
  document.addEventListener('keydown', (e) => {
    const el = e.target;
    const typing = el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
    if (isTourOpen() || document.querySelector('dialog[open]') || isCatalogOpen()) return;
    if (e.key === 'Escape') {
      if (typing) return;
      if (dock.isTrayOpen()) { dock.closeTray(); return; }
      if (tools.escape()) { e.preventDefault(); return; }
      if (layers.isDrawerOpen()) { layers.closeDrawer(); return; }
      if (panel.isOpen()) { panel.closePanel(); return; }
      if (hasApprox()) { clearApprox(); return; }
      return;
    }
    if (typing || e.isComposing) return;
    if (tools.handleKey(e)) return;
    if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); focusSearch(); return; }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const k = e.key;
    if (k === '/') { e.preventDefault(); focusSearch(); return; }
    if (k === '?') { e.preventDefault(); openHelp(); return; }
    if (/^[0-9]$/.test(k) && +k <= PLAN_LAYERS.length) { layers.setPlan(k === '0' ? null : PLAN_LAYERS[+k - 1].layerNo, { announce: true }); return; }
    switch (k.toLowerCase()) {
      case 'l': layers.toggleDrawer(); break;
      case 'b': dock.toggleTray('basemap'); break;
      case 'p': dock.toggleTray('plan'); break;
      case 'k': tools.toggle('parcel'); break;
      case 'm': tools.toggle('measure'); break;
      case 'a': tools.toggle('area'); break;
      case 'c': tools.toggle('compare'); break;
      case 'd': openCatalog(); break;
      case 's': openSaved(); break;
      case 't': toggleTheme(); break;
      case 'f': toggleFullscreen(); break;
      case '+': case '=': pm?.map?.zoomIn({ duration: 250 }); break;
      case '-': case '_': pm?.map?.zoomOut({ duration: 250 }); break;
      default: return;
    }
    // handled: keep the key from also typing into whatever the shortcut focused (e.g. "d" in the catalogue search)
    e.preventDefault();
  });
}

/* ---------------- first visit ---------------- */

function firstVisit() {
  let seen = null;
  try { seen = localStorage.getItem('qh-tour'); } catch { /* private mode */ }
  if (seen || new URLSearchParams(location.search).has('embed')) return;
  try { localStorage.setItem('qh-tour', 'seen'); } catch { /* private mode */ }
  setTimeout(() => {
    if ($('#help-modal').open || isTourOpen()) return;
    toast('Lần đầu sử dụng? Xem nhanh các thao tác chính', { type: 'info', ms: 9000, action: { label: 'Xem demo', onClick: () => openHelp() } });
  }, 1400);
}
